import * as areaPolygon from 'area-polygon';
import * as convertUnits from 'convert-units';
import {
  designerDistance,
  designerPointNearLine,
  designerPointsEqual,
  filterDesignerDuplicatePoints,
  findDesignerCommonLine,
  getDesignerDefaultTrunkHeight,
  getDesignerEdgeOrientation,
  calculateSegmentArea,
  padSegmentPolygon,
  rotateSegmentPanels as rotateDesignerSegmentPanels,
  scaleDesignerGeometry,
} from '@aerialytic/designer-states/core';

// calculate euclidean distance
export const distance = designerDistance;

export const rad2deg = (rad) => (rad * 180) / Math.PI;

export const dot = (a, b) => a.map((x, i) => a[i] * b[i]).reduce((m, n) => m + n);

// vector sum arrays
export const sumVects = (a, b) => a.map((ai, i) => ai + b[i]);
export const subVects = (a, b) => a.map((ai, i) => ai - b[i]);

// check if point is in a given shape
export const isPointInShape = (point, path) => {
  const nvert = path.length;

  // For Circle just check distance from center is less than radius
  if (nvert === 2) return distance(point, path[0]) < distance(path[0], path[1]);

  // ray-casting algorithm based on
  // https://wrf.ecse.rpi.edu/Research/Short_Notes/pnpoly.html/pnpoly.html
  const [x, y] = point;
  let inside = false;
  for (let i = 0, j = nvert - 1; i < nvert; j = i++) {
    const [xi, yi] = path[i];
    const [xj, yj] = path[j];

    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }
  return inside;
};

// check if point is on line AB
export const isPointOnLine = (point, A, B) => (
  designerPointNearLine(point, A, B, 1.25)
);

export const isPointNearLine = designerPointNearLine;

// check if point is along path
export const isPointInPath = (point, path) => {
  let lineIndex = -1;

  path.forEach((_, index) => {
    // check distance from between vertices ==
    // the sum of distances from each vertex to cursor
    if (index < path.length - 1) {
      if (isPointOnLine(point, path[index], path[index + 1])) {
        lineIndex = index;
        return;
      }
      // check line that closes the path
    } else if (isPointOnLine(point, path[index], path[0])) {
      lineIndex = index;
    }
  });
  return lineIndex;
};

// scales geometry of given shape
export const scaleShape = (shape, factor, retGeom = false) => {
  const scaled = scaleDesignerGeometry(shape.geometry, factor);
  if (retGeom) return scaled;
  return { ...shape, geometry: scaled };
};

export const isPointEqual = designerPointsEqual;

export const filterDuplicates = filterDesignerDuplicatePoints;

// TODO: re-write this function to detect "overlapping" lines more generally
export const findCommonLine = findDesignerCommonLine;

export const inchToPx = (inches, resolution) => (inches * 2.54) / resolution;

export const inchToCm = (inch) => (inch * 2.54);

// fix azimuth orientation of panels on a roof
export const rotateSegmentPanels = rotateDesignerSegmentPanels;

export const appendArc = (vertices, center, radius, startVertex, endVertex, isPaddingBoundary) => {
  const twoPI = Math.PI * 2;
  let startAngle = Math.atan2(startVertex[1] - center[1], startVertex[0] - center[0]);
  let endAngle = Math.atan2(endVertex[1] - center[1], endVertex[0] - center[0]);
  if (startAngle < 0) startAngle += twoPI;
  if (endAngle < 0) endAngle += twoPI;
  const arcSegmentCount = 5; // Odd number so one arc vertex will be exactly arcRadius from center
  const angle = (startAngle > endAngle) ? (startAngle - endAngle) : (startAngle + twoPI - endAngle);
  const angle5 = ((isPaddingBoundary) ? -angle : twoPI - angle) / arcSegmentCount;

  vertices.push(startVertex);
  for (let i = 1; i < arcSegmentCount; i += 1) {
    const theta = startAngle + angle5 * i;
    const vertex = [
      center[0] + Math.cos(theta) * radius,
      center[1] + Math.sin(theta) * radius,
    ];
    vertices.push(vertex);
  }
  vertices.push(endVertex);
};

export const edgeIntersection = (edgeA, edgeB) => {
  // TODO: clean this up
  const den = (edgeB[1][1] - edgeB[0][1]) * (edgeA[1][0] - edgeA[0][0])
    - (edgeB[1][0] - edgeB[0][0]) * (edgeA[1][1] - edgeA[0][1]);
  // if (den == 0)
  //   return null;  // lines are parallel or coincident

  const ua = ((edgeB[1][0] - edgeB[0][0]) * (edgeA[0][1] - edgeB[0][1])
    - (edgeB[1][1] - edgeB[0][1]) * (edgeA[0][0] - edgeB[0][0])) / den;
  // let ub = ((edgeA[1][0] - edgeA[0][0]) * (edgeA[0][1] - edgeB[0][1])
  // - (edgeA[1][1] - edgeA[0][1]) * (edgeA[0][0] - edgeB[0][0])) / den;

  // if (ua < 0 || ub < 0 || ua > 1 || ub > 1)
  //   return null;

  return [
    edgeA[0][0] + ua * (edgeA[1][0] - edgeA[0][0]),
    edgeA[0][1] + ua * (edgeA[1][1] - edgeA[0][1]),
  ];
};

export const offsetEdge = (ept1, ept2, diff) => {
  return [
    [ept1[0] + diff[0], ept1[1] + diff[1]],
    [ept2[0] + diff[0], ept2[1] + diff[1]],
  ];
};

export const rotateArrayRight = (arr, k) => {
  for (let i = 0; i < k; i += 1) {
    const last = arr.pop();
    arr.unshift(last);
  }
  return arr;
};

export const rotateArrayLeft = (arr, k) => {
  for (let i = 0; i < k; i += 1) {
    const first = arr.shift();
    arr.push(first);
  }
  return arr;
};

export const rotateLeft = (arr) => {
  const first = arr.shift();
  arr.push(first);
  return arr;
};

export const rotateRight = (arr) => {
  const last = arr.pop();
  arr.unshift(last);
  return arr;
};

/* export const paddingAroundPolygon = (shape) => {

}; */

export const orthoProject = (a, b, c) => {
  let mr = 1000000000;

  if (b[0] - a[0] !== 0) {
    mr = (b[1] - a[1]) / (b[0] - a[0]);
  }

  let ms = -10000000000;
  if (mr !== 0) {
    ms = (-1) / mr;
  }

  const a1 = -mr;
  // const b1 = 1;
  const c1 = a[1] - mr * a[0];

  const a2 = -ms;
  // const b2 = 1;
  const c2 = c[1] - ms * c[0];

  const X = Math.round((c1 - c2) / (a1 - a2));
  const Y = Math.round((a1 * c2 - a2 * c1) / (a1 - a2));

  return [X, Y];
};

const dist2 = (v, w) => (v[0] - w[0]) ** 2 + (v[1] - w[1]) ** 2;

function distToSegmentSquared(p, v, w) {
  const l2 = dist2(v, w);
  if (l2 === 0) return dist2(p, v);
  let t = ((p[0] - v[0]) * (w[0] - v[0]) + (p[1] - v[1]) * (w[1] - v[1])) / l2;
  t = Math.max(0, Math.min(1, t));
  return dist2(p, [v[0] + t * (w[0] - v[0]), v[0] + t * (w[0] - v[0])]);
}

export const distToLine = (p, v, w) => Math.sqrt(distToSegmentSquared(p, v, w));

export const translateVector = (origin, pt, rotation) => {
  const angle = rotation * (Math.PI / 180); // convert to radians
  const [x3, y3] = [
    (Math.cos(angle) * (pt[0] - origin[0]) - Math.sin(angle) * (pt[1] - origin[1])) + origin[0],
    (Math.sin(angle) * (pt[0] - origin[0]) + Math.cos(angle) * (pt[1] - origin[1])) + origin[1],
  ];
  return [x3, y3];
};

export const getKonvaRectCorners = (rect) => {
  const { x, y } = rect.position();
  const rotation = rect.rotation();
  const width = rect.width();
  const height = rect.height();

  return [
    [x, y],
    translateVector([x, y], [x + width, y], rotation),
    translateVector([x, y], [x + width, y + height], rotation),
    translateVector([x, y], [x, y + height], rotation),
  ];
};

export const drawOrientation = getDesignerEdgeOrientation;

export const inwardEdgeNormal = (geometry) => {
  const ori = drawOrientation(geometry, 0);

  const inwardNormals = geometry.map((pt, jdx) => {
    let dx = geometry[(jdx + 1) % geometry.length][0] - pt[0];
    let dy = geometry[(jdx + 1) % geometry.length][1] - pt[1];
    const edgeLength = Math.sqrt(dx * dx + dy * dy);
    if (ori === 'ccw') {
      dx = -dx;
      dy = -dy;
    }
    return [-dy / edgeLength, dx / edgeLength];
  });
  return inwardNormals;
};

export const mindDistanceToLine = (E, A, B) => {
  /*
    E: Target point
    A: Line point 1
    B: Line point 2
  */
  // vector AB
  const AB = [NaN, NaN];
  AB[0] = B[0] - A[0];
  AB[1] = B[1] - A[1];

  // vector BP
  const BE = [NaN, NaN];
  BE[0] = E[0] - B[0];
  BE[1] = E[1] - B[1];

  // vector AP
  const AE = [NaN, NaN];
  AE[0] = E[0] - A[0];
  AE[1] = E[1] - A[1];
  // Variables to store dot product
  // Calculating the dot product
  const AB_BE = AB[0] * BE[0] + AB[1] * BE[1];
  const AB_AE = AB[0] * AE[0] + AB[1] * AE[1];
  // Minimum distance from
  // point E to the line segment
  let reqAns = 0;
  if (AB_BE > 0) {
    // Case 1
    // Finding the magnitude
    const y = E[1] - B[1];
    const x = E[0] - B[0];
    reqAns = Math.sqrt(x * x + y * y);
  } else if (AB_AE < 0) {
    // Case 2
    const y = E[1] - A[1];
    const x = E[0] - A[0];
    reqAns = Math.sqrt(x * x + y * y);
  } else {
    // Case 3
    // Finding the perpendicular distance
    const x1 = AB[0];
    const y1 = AB[1];
    const x2 = AE[0];
    const y2 = AE[1];
    const mod = Math.sqrt(x1 * x1 + y1 * y1);
    reqAns = Math.abs(x1 * y2 - y1 * x2) / mod;
  }
  return reqAns;
};

export const padPolygon = padSegmentPolygon;

const delay = (fn, ms) => new Promise((resolve) => setTimeout(() => resolve(fn()), ms));

const randInt = (min, max) => Math.floor(Math.random() * (max - min + 1) + min);

export const fail = () => {
  throw new Error('Failed to retrieve data from NREL.');
};

export const retry = async (fn, maxAttempts) => {
  const execute = async (attempt) => {
    try {
      return await fn();
    } catch (err) {
      if (attempt <= maxAttempts) {
        const nextAttempt = attempt + 1;
        const delayInSeconds = Math.max(Math.min((2 ** nextAttempt) + randInt(-nextAttempt, nextAttempt), 600), 1);
        console.log(`Retrying after ${delayInSeconds} seconds due to:`, err);
        return delay(() => execute(nextAttempt), delayInSeconds * 1000);
      }
      throw err;
    }
  };
  return execute(1);
};

export const ios = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  const isIos = /iPhone|iPad|iPod|iOS/i.test(navigator.userAgent || navigator.vendor);
  return isIos;
};

export const getSegmentArea = calculateSegmentArea;

export const getSegmentArea_ = (segment) => {
  const samplingRate = 30;
  const P = Math.tan(Number(segment.pitch) * 0.0174533) * 12;
  const slopeFactor = Math.sqrt(P * P + 12 * 12) / 12;
  const segArea = areaPolygon(segment.geometry) * slopeFactor;
  const segAreaSqm = (segArea * samplingRate) / 10000;
  const area = convertUnits(segAreaSqm).from('m2').to('ft2');
  if (isNaN(area)) area = 0;
  return area;
};

export const updateSegmentArea = (segments, resolution) => {
  segments.forEach((segment) => {
    if (isNaN(segment.area)) segment.area = getSegmentArea(segment, resolution);
  });
};

export const isEmpty = (obj) => {
  for (const prop in obj) {
    if (Object.hasOwn(obj, prop)) {
      return false;
    }
  }

  return true;
};

export const getDefaultTrunkHeight = getDesignerDefaultTrunkHeight;
